import { Injectable, NgZone } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Router } from "@angular/router";
import { Capacitor } from "@capacitor/core";
import {
  ActionPerformed,
  PushNotifications,
  PushNotificationSchema,
  Token,
} from "@capacitor/push-notifications";
import { ToastController } from "@ionic/angular";
import { map } from "rxjs/operators";
import { Device } from "@capacitor/device";
import { domain } from "../variables/domain";
import { SharedServices } from "./shared.service";
import { AdminService } from "./admin.service";

export const PUSH_TOKEN_STORAGE_KEY = "AAAAvLB8-zs:APA91bHNzaD78bU3ymsPFpWtdr9UXtZbc87Q_7N8VlHuh5PssSOdP8o6KoSNoZ-ONPbqWSS-lpQmi1qlr8NC5ADA_JhlNTzQnfYRHNZKTUPwfjqAo-QpAnIULnp9xWzKx05ox_X4xZ3Z";
export const DEFAULT_CHANNEL_ID = "default_channel";

export interface DeviceRegistrationPayload {
  Token: string;
  Platform: string; // "android" | "ios"
  UserId: any;
}

@Injectable({
  providedIn: "root",
})
export class PushNotificationService {
  private deviceToken: string | null = null;
  private listenersAdded = false;

  constructor(
    private http: HttpClient,
    private router: Router,
    private zone: NgZone,
    private toastController: ToastController,
    private sharedServices: SharedServices,
    private adminService: AdminService
  ) {}

  /**
   * Entry point — call once on app startup.
   * Runs only on native platforms (Android / iOS).
   */
  async initPushNotifications(): Promise<void> {
    try {
      if (!Capacitor.isNativePlatform()) {
        return;
      }

      try {
        this.addListeners();
      } catch (error) {
        console.error("Failed to attach push notification listeners:", error);
      }

      try {
        let permStatus = await PushNotifications.checkPermissions();

        if (permStatus.receive === "prompt") {
          permStatus = await PushNotifications.requestPermissions();
        }

        // if (permStatus.receive !== "granted") {
        //   // Edge case: user denied notification permission
        //   this.showToast(
        //     "Notification permission denied. You can enable it from system settings to receive updates.",
        //     "warning",
        //     4000
        //   );
        //   return;
        // }

        // Registers with FCM on Android and APNs on iOS.
        // The resulting token arrives via the "registration" listener.
        await this.createDefaultChannel();
        await PushNotifications.register();
      } catch (error) {
        console.error("Push notification setup failed:", error);
        // this.showToast(
        //   "Unable to set up push notifications. Please try again later.",
        //   "danger"
        // );
      }
    } catch (error) {
      // Guarantees initPushNotifications() never rejects/throws to its caller.
      console.error("Unexpected error initializing push notifications:", error);
    }
  }

  /**
   * Android 8+: high-importance channel so notifications show as a heads-up
   * banner and play the device's default notification sound. No-op on iOS.
   */
  private async createDefaultChannel(): Promise<void> {
    if (Capacitor.getPlatform() !== "android") {
      return;
    }
    try {
      await PushNotifications.createChannel({
        id: DEFAULT_CHANNEL_ID,
        name: "Default",
        description: "General notifications",
        importance: 5, // HIGH -> heads-up banner
        visibility: 1, // show on lock screen
        // sound omitted -> uses the system default notification sound
        vibration: true,
        lights: true,
      });
    } catch (e) {
      // Best-effort; falls back to the system default channel if this fails.
      console.error("Failed to create push notification channel:", e);
    }
  }

  /** Returns the last known FCM/APNs device token (null until registration completes). */
  getDeviceToken(): string | null {
    try {
      return this.deviceToken ?? localStorage.getItem(PUSH_TOKEN_STORAGE_KEY);
    } catch (error) {
      console.error("Failed to read stored push token:", error);
      return this.deviceToken ?? null;
    }
  }

  /** Re-runs the registration flow (useful after login to bind token to the user). */
  async refreshRegistration(): Promise<void> {
    try {
      await this.initPushNotifications();
    } catch (error) {
      // initPushNotifications() already self-handles errors; this is a final safety net.
      console.error("Failed to refresh push notification registration:", error);
    }
  }

  private addListeners(): void {
    if (this.listenersAdded) {
      return;
    }
    this.listenersAdded = true;

    // Fires on initial registration AND whenever FCM/APNs refreshes the token.
    PushNotifications.addListener("registration", (token: Token) => {
      try {
        const platform = Capacitor.getPlatform(); // "android" | "ios"
        let previousToken: string | null = null;
        try {
          previousToken = localStorage.getItem(PUSH_TOKEN_STORAGE_KEY);
        } catch (error) {
          console.error("Failed to read previous push token:", error);
        }

        this.deviceToken = token.value;
        try {
          localStorage.setItem(PUSH_TOKEN_STORAGE_KEY, token.value);
        } catch (error) {
          console.error("Failed to persist push token:", error);
        }

        // Token refresh edge case: re-register with backend when the token changes.
        if (token.value !== previousToken || !previousToken) {
          this.registerDeviceWithBackend(token.value, platform).catch((error) => {
            console.error("Device registration with backend failed:", error);
          });
        }
      } catch (error) {
        console.error("Error handling push registration token:", error);
      }
    });

    PushNotifications.addListener("registrationError", (error: any) => {
      console.error("Push notification registration error:", error);
      // this.showToast(
      //   "Push notification registration failed. Notifications may not work on this device.",
      //   "danger"
      // );
    });

    // Foreground notification received while the app is open.
    PushNotifications.addListener(
      "pushNotificationReceived",
      (notification: PushNotificationSchema) => {
        try {
          const title = notification.title || "Notification";
          const body = notification.body || "";
          // this.showToast(
          //   body ? `${title}: ${body}` : title,
          //   "primary",
          //   4000
          // );
        } catch (error) {
          console.error("Error handling received push notification:", error);
        }
      }
    );

    // User tapped / acted on a notification (from tray or in-app).
    PushNotifications.addListener(
      "pushNotificationActionPerformed",
      (action: ActionPerformed) => {
        try {
          const data = action.notification?.data;
          this.navigateFromNotification(data);
        } catch (error) {
          console.error("Error handling push notification action:", error);
        }
      }
    );
  }

  /** Sends the device token + platform to the backend via AdminService.AddUserDevice. */
  async registerDeviceWithBackend(token: string, platform: string): Promise<void> {
    try {
      let deviceId = "";
      try {
        const idInfo = await Device.getId();
        deviceId = idInfo?.identifier || "";
      } catch (e) {
        // Best-effort; register even if the device id is unavailable.
        console.error("Failed to read device id:", e);
      }

      const payload = {
        deviceType: platform === "android" ? "Android" : "iOS",
        deviceToken: token,
        mobileDeviceId: deviceId,
      };

      try {
        this.adminService.AddUserDevice(payload).subscribe({
          next: () => {
            // Device registered successfully — no user-facing action needed.
          },
          error: (error) => {
            console.error("AddUserDevice API call failed:", error);
            // this.showToast(
            //   "Could not register this device for notifications. It will retry on next app launch.",
            //   "warning"
            // );
          },
        });
      } catch (error) {
        // Guards against a synchronous failure building/issuing the HTTP request.
        console.error("Failed to issue AddUserDevice request:", error);
      }
    } catch (error) {
      console.error("Unexpected error registering device with backend:", error);
    }
  }

  /** Navigates to the relevant page using the notification payload data. */
  private navigateFromNotification(data: any): void {
    try {
      if (!data) {
        return;
      }
      const target = data.route || data.url || data.redirect;
      if (!target || typeof target !== "string") {
        return;
      }

      // Push callbacks run outside Angular's zone — re-enter it so navigation works.
      this.zone.run(() => {
        try {
          const navigation =
            data.queryParams && typeof data.queryParams === "object"
              ? this.router.navigate([target], { queryParams: data.queryParams })
              : this.router.navigateByUrl(target);

          navigation.catch((error) => {
            console.error("Navigation from push notification failed:", error);
          });
        } catch (error) {
          console.error("Error navigating from push notification:", error);
        }
      });
    } catch (error) {
      console.error("Error processing push notification navigation data:", error);
    }
  }

  private getCurrentAgentId(): any {
    try {
      const agentId = this.sharedServices.getAgentId();
      return agentId ? agentId : null;
    } catch (e) {
      console.error("Failed to read current agent id:", e);
      return null;
    }
  }

  private async showToast(
    message: string,
    color: string = "primary",
    duration: number = 3000
  ): Promise<void> {
    try {
      const toast = await this.toastController.create({
        message,
        duration,
        position: "top",
        color,
      });
      await toast.present();
    } catch (error) {
      console.error("Failed to show toast:", error);
    }
  }
}
